import axios from 'axios';
import { AccountInfo, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { ImpersonationStore } from './ImpersonationStore';
import { StudentAccess } from './ImpersonationStore';

export type AuthSession = {
  access_token: string;
  expires: number;
  starId: string;
  displayName?: string;
  roles: string[];
  canImpersonate: boolean;
  isAccessAdministrator?: boolean;
  isGraduateAdministrator?: boolean;
  isUndergraduateAdministrator?: boolean;
  canEvaluateUndergraduate?: boolean;
  canViewUndergraduateApplications?: boolean;
  canEvaluateGraduate?: boolean;
  canReviewGraduateFaculty?: boolean;
  studentAccess?: StudentAccess;
};

type AuthOptions = { interactive?: boolean; forceRefresh?: boolean };
const trimQuotes = (value = '') => String(value || '').replace(/^['"]|['"]$/g, '');
const apiBaseValue = trimQuotes(process.env.REACT_APP_API_BASE) || 'https://localhost:7192/api/';
const apiBase = apiBaseValue.endsWith('/') ? apiBaseValue : `${apiBaseValue}/`;
const tenantId = trimQuotes(process.env.REACT_APP_AZURE_TENANT_ID);
const clientId = trimQuotes(process.env.REACT_APP_AZURE_CLIENT_ID);
const apiScope = trimQuotes(process.env.REACT_APP_AZURE_API_SCOPE);
const redirectUri = trimQuotes(process.env.REACT_APP_AZURE_REDIRECT_URI) || window.location.href.split('#')[0];
const postLogoutRedirectUri = trimQuotes(process.env.REACT_APP_AZURE_POST_LOGOUT_REDIRECT_URI) || `${redirectUri}#/login`;
const storageKeys = { token: 'graduationToken', redirect: 'graduationPostLoginRedirect' };

const isPlaceholder = (value: string) => !value || value.startsWith('__') || value === 'undefined';
const ensureConfigured = () => {
  const missing = [
    ['REACT_APP_AZURE_TENANT_ID', tenantId],
    ['REACT_APP_AZURE_CLIENT_ID', clientId],
    ['REACT_APP_AZURE_API_SCOPE', apiScope],
  ].filter(([, value]) => isPlaceholder(value)).map(([name]) => name);
  if (missing.length) throw new Error(`Microsoft Entra configuration is missing: ${missing.join(', ')}.`);
};

const msal = new PublicClientApplication({
  auth: {
    clientId: isPlaceholder(clientId) ? '00000000-0000-0000-0000-000000000000' : clientId,
    authority: `https://login.microsoftonline.com/${isPlaceholder(tenantId) ? 'common' : tenantId}`,
    redirectUri,
    postLogoutRedirectUri,
  },
  cache: { cacheLocation: 'localStorage' },
});
const loginRequest = { scopes: isPlaceholder(apiScope) ? [] : [apiScope] };
let initializePromise: Promise<AuthSession | null> | null = null;
let refreshPromise: Promise<AuthSession | null> | null = null;
let sessionRefreshPromise: Promise<void> | null = null;

const getAccount = (): AccountInfo | null => {
  const account = msal.getActiveAccount() || msal.getAllAccounts()[0] || null;
  if (account) msal.setActiveAccount(account);
  return account;
};
const getCurrentToken = (): AuthSession | null => {
  try { return JSON.parse(localStorage.getItem(storageKeys.token) || 'null'); }
  catch { return null; }
};
const saveSession = (session: AuthSession) => {
  if (getCurrentToken()?.starId && getCurrentToken()?.starId !== session.starId) ImpersonationStore.set(null);
  localStorage.setItem(storageKeys.token, JSON.stringify(session));
  axios.defaults.headers.common.Authorization = `Bearer ${session.access_token}`;
  window.dispatchEvent(new Event('graduation-auth-updated'));
  return session;
};
const logoutLocal = () => {
  ImpersonationStore.set(null);
  localStorage.removeItem(storageKeys.token);
  delete axios.defaults.headers.common.Authorization;
  window.dispatchEvent(new Event('graduation-auth-updated'));
};
const subscribe = (listener: () => void) => {
  window.addEventListener('graduation-auth-updated', listener);
  return () => window.removeEventListener('graduation-auth-updated', listener);
};
const sessionSnapshot = () => localStorage.getItem(storageKeys.token);
const refreshCurrentSession = () => {
  if (sessionRefreshPromise) return sessionRefreshPromise;
  sessionRefreshPromise = (async () => {
    const current = getCurrentToken();
    if (current?.access_token && current.expires > Math.floor(Date.now() / 1000) + 60)
      await fetchSession(current.access_token);
  })().finally(() => { sessionRefreshPromise = null; });
  return sessionRefreshPromise;
};
const fetchSession = async (accessToken: string) => {
  const response = await axios.get<AuthSession>(`${apiBase}authenticate`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    skipAuthorization: true,
    skipAuthRedirect: true,
  });
  return saveSession({ ...response.data, access_token: accessToken });
};
const acquireSession = async ({ interactive = false, forceRefresh = false }: AuthOptions = {}) => {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    ensureConfigured();
    const account = getAccount();
    if (!account) return null;
    try {
      const result = await msal.acquireTokenSilent({ ...loginRequest, account, forceRefresh });
      return fetchSession(result.accessToken);
    } catch (error) {
      if (interactive && error instanceof InteractionRequiredAuthError) {
        await msal.acquireTokenRedirect(loginRequest);
        return null;
      }
      throw error;
    }
  })().finally(() => { refreshPromise = null; });
  return refreshPromise;
};
const initialize = () => {
  if (initializePromise) return initializePromise;
  initializePromise = (async () => {
    await msal.initialize();
    const redirect = await msal.handleRedirectPromise();
    if (redirect?.account) msal.setActiveAccount(redirect.account);
    if (redirect?.accessToken) return fetchSession(redirect.accessToken);
    if (!getAccount()) { logoutLocal(); return null; }
    return acquireSession();
  })();
  return initializePromise;
};
const login = async () => {
  ensureConfigured();
  const session = await acquireSession({ interactive: true });
  if (session) return session;
  await msal.loginRedirect(loginRequest);
  return null;
};
const logout = async () => {
  const account = getAccount();
  logoutLocal();
  if (account) await msal.logoutRedirect({ account, postLogoutRedirectUri });
  else window.location.hash = '#/login';
};
const getAccessToken = async (options: AuthOptions = {}) => {
  const current = getCurrentToken();
  if (!options.forceRefresh && current?.access_token && current.expires > Math.floor(Date.now() / 1000) + 60) return current.access_token;
  return (await acquireSession(options))?.access_token || null;
};
const isTokenValid = () => Boolean(getCurrentToken()?.expires && getCurrentToken()!.expires > Math.floor(Date.now() / 1000));
const setPostLoginRedirectPath = (path: string) => sessionStorage.setItem(storageKeys.redirect, path);
const consumePostLoginRedirectPath = () => {
  const path = sessionStorage.getItem(storageKeys.redirect);
  sessionStorage.removeItem(storageKeys.redirect);
  return path;
};

const AuthService = {
  initialize,
  login,
  logout,
  logoutLocal,
  getAccessToken,
  getCurrentToken,
  subscribe,
  sessionSnapshot,
  refreshCurrentSession,
  isTokenValid,
  setPostLoginRedirectPath,
  consumePostLoginRedirectPath,
};

export default AuthService;
